import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { soundService } from '../../services/soundService';
import { trackEvent } from '../../services/analyticsService';
import { hapticService } from '../../services/hapticService';

interface TimerState {
  timeLeft: number;
  isRunning: boolean;
  mode: 'work' | 'break';
  lastUpdated: number;
}

interface FocusPageProps {
  onRequestFullscreen?: (isFullscreen: boolean) => void;
}

const FocusPage: React.FC<FocusPageProps> = ({ onRequestFullscreen }) => {
  const { completePomodoroSession } = useUser();
  const { t } = useLanguage();
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // Customizable durations (in minutes, converted to seconds)
  const [workDurationMin, setWorkDurationMin] = useState(() => {
    const saved = localStorage.getItem('pomodoroWorkDuration');
    return saved ? parseInt(saved) : 25;
  });
  const [breakDurationMin, setBreakDurationMin] = useState(() => {
    const saved = localStorage.getItem('pomodoroBreakDuration');
    return saved ? parseInt(saved) : 5;
  });
  
  const workDuration = workDurationMin * 60;
  const breakDuration = breakDurationMin * 60;
  
  const [showSettings, setShowSettings] = useState(false);
  
  // Common Pomodoro presets
  const workPresets = [15, 25, 30, 45, 50, 60];
  const breakPresets = [5, 10, 15, 20];
  
  // Load initial state from localStorage or use defaults
  const loadTimerState = (): TimerState => {
    const saved = localStorage.getItem('focusTimerState');
    if (saved) {
      try {
        const state: TimerState = JSON.parse(saved);
        // If timer was running, calculate elapsed time since last update
        if (state.isRunning) {
          const now = Date.now();
          const elapsedSeconds = Math.floor((now - state.lastUpdated) / 1000);
          const newTimeLeft = Math.max(0, state.timeLeft - elapsedSeconds);
          return {
            ...state,
            timeLeft: newTimeLeft,
            lastUpdated: now,
          };
        }
        return state;
      } catch (error) {
        console.error('Failed to parse timer state:', error);
      }
    }
    return {
      timeLeft: 25 * 60,
      isRunning: false,
      mode: 'work',
      lastUpdated: Date.now(),
    };
  };

  const initialState = loadTimerState();
  const [timeLeft, setTimeLeft] = useState(initialState.timeLeft);
  const [isRunning, setIsRunning] = useState(initialState.isRunning);
  const [mode, setMode] = useState<'work' | 'break'>(initialState.mode);

  // Responsive detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const mobile = width < 640; // < sm
      const tablet = width >= 640 && width < 1024; // sm..md/lg
      setIsMobile(mobile);
      setIsTablet(tablet);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Disable fullscreen on mobile
  useEffect(() => {
    if (isMobile && isFullscreen) {
      setIsFullscreen(false);
    }
  }, [isMobile, isFullscreen]);

  // Notify parent about fullscreen state
  useEffect(() => {
    if (onRequestFullscreen) {
      onRequestFullscreen(isFullscreen);
    }
  }, [isFullscreen, onRequestFullscreen]);

  // Save timer state to localStorage whenever it changes
  useEffect(() => {
    const state: TimerState = {
      timeLeft,
      isRunning,
      mode,
      lastUpdated: Date.now(),
    };
    localStorage.setItem('focusTimerState', JSON.stringify(state));
  }, [timeLeft, isRunning, mode]);

  // Save duration settings to localStorage
  useEffect(() => {
    localStorage.setItem('pomodoroWorkDuration', workDurationMin.toString());
    localStorage.setItem('pomodoroBreakDuration', breakDurationMin.toString());
  }, [workDurationMin, breakDurationMin]);

  // Update timer when user changes duration in settings (only if not running)
  useEffect(() => {
    if (!isRunning) {
      if (mode === 'work') {
        setTimeLeft(workDuration);
      } else if (mode === 'break') {
        setTimeLeft(breakDuration);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workDurationMin, breakDurationMin]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleTimerComplete();
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, timeLeft]);

  const handleTimerComplete = () => {
    setIsRunning(false);
    if (mode === 'work') {
      completePomodoroSession(workDurationMin);
      soundService.focusEnd();
      
      trackEvent('focus_session_completed', {
        duration_minutes: workDurationMin,
        mode: 'work'
      });
      
      setMode('break');
      setTimeLeft(breakDuration);
    } else {
      setMode('work');
      setTimeLeft(workDuration);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return {
      minutes: mins.toString().padStart(2, '0'),
      seconds: secs.toString().padStart(2, '0')
    };
  };

  const progress = mode === 'work'
    ? ((workDuration - timeLeft) / workDuration) * 100
    : ((breakDuration - timeLeft) / breakDuration) * 100;

  const time = formatTime(timeLeft);

  // Motivational quotes
  const workQuotes = [
    "Deep work produces deep results.",
    "Focus is your superpower.",
    "One focused hour beats ten distracted.",
    "Progress happens in the present moment.",
    "Your attention is your most valuable asset.",
  ];
  
  const breakQuotes = [
    "Rest sharpens the mind.",
    "Breaks are part of productivity.",
    "Recharge to perform at your best.",
    "Recovery is progress.",
    "Step back to leap forward.",
  ];
  
  const currentQuote = mode === 'work' 
    ? workQuotes[Math.floor(Date.now() / 3600000) % workQuotes.length]
    : breakQuotes[Math.floor(Date.now() / 3600000) % breakQuotes.length];

  const containerClass = isFullscreen 
    ? "fixed inset-0 w-screen h-screen z-[100]" 
    : "animate-fade-in px-4 sm:px-6";

  return (
    <div className={containerClass}>
      {/* Normal Mode - Modern Card Layout */}
      {!isFullscreen && (
          <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6" style={{ filter: 'contrast(0.95) brightness(0.95)' }}>
      {/* Header */}
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse shadow-neon-blue"></div>
                <span className="font-orbitron text-2xl tracking-wider text-system-blue uppercase">
            ［ {t.focus.title} ］
          </span>
        </div>
              <p className="text-sm text-gray-400 font-rajdhani">
            {t.focus.subtitle}
          </p>
            </div>
            
            <div className="flex gap-2 sm:gap-3">
          <button
                onClick={() => {
                  hapticService.tap();
                  setShowSettings(!showSettings);
                }}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-700/70 border-b-slate-700/70 rounded-2xl font-orbitron text-xs sm:text-sm font-bold transition-all hover:scale-105 relative"
                style={{
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.06), inset 0 -1px 3px rgba(0, 0, 0, 0.3)'
                }}
              >
                <span className="mr-2 relative z-10">⚙️</span>
                <span className="hidden sm:inline relative z-10">SETTINGS</span>
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
          </button>
              
              {!isMobile && (
              <button
                onClick={() => {
                  hapticService.click();
                  setIsFullscreen(true);
                }}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-b from-blue-800/50 via-purple-800/40 to-blue-950/50 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 rounded-2xl font-orbitron text-xs sm:text-sm font-bold transition-all hover:scale-105 relative"
                style={{
                  boxShadow: '0 6px 18px rgba(139, 92, 246, 0.5), 0 2px 6px rgba(139, 92, 246, 0.3), inset 0 1px 3px rgba(196, 181, 253, 0.15), inset 0 -2px 6px rgba(88, 28, 135, 0.4)'
                }}
              >
                <span className="mr-2 relative z-10">🖥️</span>
                <span className="hidden sm:inline relative z-10">FULLSCREEN</span>
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
          </button>
              )}
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
            <div 
              className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6 animate-fade-in max-h-[70vh] overflow-y-auto"
              style={{
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="font-orbitron font-bold text-lg sm:text-xl text-system-blue">⚙️ TIMER SETTINGS</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-8 h-8 rounded-lg bg-gradient-to-b from-slate-800/60 to-slate-900/60 border-2 border-slate-700/60 hover:border-slate-600 flex items-center justify-center text-gray-400 hover:text-white transition-all"
                  style={{
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.06)'
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
                {/* Work Duration */}
                <div>
                  <label className="block text-sm font-rajdhani text-gray-400 uppercase tracking-wider mb-4">
                    Focus Duration
                  </label>
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="text-4xl sm:text-5xl font-black font-orbitron text-system-blue">
                      {workDurationMin}
                    </span>
                    <span className="text-base sm:text-lg text-gray-400 font-rajdhani">minutes</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {workPresets.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setWorkDurationMin(preset)}
                    disabled={isRunning}
                        className={`px-3 py-2 sm:px-4 sm:py-2 rounded-2xl font-orbitron text-xs sm:text-sm font-bold transition-all relative ${
                      workDurationMin === preset
                            ? 'bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 text-white scale-105'
                            : 'bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:text-white hover:scale-105'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={workDurationMin === preset ? {
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), 0 1px 4px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(100, 150, 255, 0.15), inset 0 -1px 3px rgba(30, 58, 138, 0.4)'
                    } : {
                      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.06), inset 0 -1px 2px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <span className="relative z-10">{preset}m</span>
                    {workDurationMin === preset && (
                      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Break Duration */}
            <div>
                  <label className="block text-sm font-rajdhani text-gray-400 uppercase tracking-wider mb-4">
                Break Duration
              </label>
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="text-4xl sm:text-5xl font-black font-orbitron text-system-purple">
                  {breakDurationMin}
                </span>
                    <span className="text-base sm:text-lg text-gray-400 font-rajdhani">minutes</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {breakPresets.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setBreakDurationMin(preset)}
                    disabled={isRunning}
                        className={`px-3 py-2 sm:px-4 sm:py-2 rounded-2xl font-orbitron text-xs sm:text-sm font-bold transition-all relative ${
                      breakDurationMin === preset
                            ? 'bg-gradient-to-b from-purple-800/60 via-purple-900/50 to-purple-950/60 border-t-2 border-l-2 border-purple-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 text-white scale-105'
                            : 'bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:text-white hover:scale-105'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={breakDurationMin === preset ? {
                      boxShadow: '0 4px 12px rgba(168, 85, 247, 0.4), 0 1px 4px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(196, 181, 253, 0.15), inset 0 -1px 3px rgba(88, 28, 135, 0.4)'
                    } : {
                      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.06), inset 0 -1px 2px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <span className="relative z-10">{preset}m</span>
                    {breakDurationMin === preset && (
                      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

          {/* Main Timer Card */}
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left - Mode Selection */}
            <div className="space-y-3 sm:space-y-4">
        <button
          onClick={() => {
            if (mode !== 'work') {
                    hapticService.tap();
              setMode('work');
              setTimeLeft(workDuration);
              setIsRunning(false);
            }
          }}
                className={`w-full p-4 sm:p-6 rounded-2xl font-orbitron font-bold tracking-wider transition-all ${
            mode === 'work'
                    ? 'bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 text-white scale-105'
                    : 'bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:border-blue-500/50 hover:text-white hover:scale-105'
                }`}
                    style={mode === 'work' ? {
                      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(59, 130, 246, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.5)'
                } : {
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">💼</div>
                <div className="text-base sm:text-lg">{t.focus.work}</div>
                {mode === 'work' && (
                  <div className="text-xs mt-2 text-blue-200 font-rajdhani">ACTIVE</div>
                )}
                {mode === 'work' && (
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
                )}
        </button>
              
        <button
          onClick={() => {
            if (mode !== 'break') {
                    hapticService.tap();
              setMode('break');
              setTimeLeft(breakDuration);
              setIsRunning(false);
            }
          }}
                className={`w-full p-4 sm:p-6 rounded-2xl font-orbitron font-bold tracking-wider transition-all relative ${
            mode === 'break'
                    ? 'bg-gradient-to-b from-purple-800/60 via-purple-900/50 to-purple-950/60 border-t-2 border-l-2 border-purple-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 text-white scale-105'
                    : 'bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:border-purple-500/50 hover:text-white hover:scale-105'
                }`}
                style={mode === 'break' ? {
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(168, 85, 247, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.5)'
                } : {
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">☕</div>
                <div className="text-base sm:text-lg">{t.focus.break}</div>
                {mode === 'break' && (
                  <div className="text-xs mt-2 text-purple-200 font-rajdhani">ACTIVE</div>
                )}
                {mode === 'break' && (
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
                )}
        </button>

              {/* Rewards Card */}
              <div 
                className="bg-gradient-to-b from-yellow-950/40 via-yellow-900/30 to-yellow-950/40 backdrop-blur-sm border-t-2 border-l-2 border-yellow-500/40 border-r-2 border-b-2 border-r-yellow-700/60 border-b-yellow-700/60 rounded-3xl p-3 sm:p-4"
                style={{
                  boxShadow: '0 6px 18px rgba(234, 179, 8, 0.3), 0 2px 6px rgba(234, 179, 8, 0.2), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="text-xs text-gray-400 font-rajdhani uppercase mb-3 tracking-wider">
                  Session Rewards
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Experience</span>
                    <span className="text-system-blue font-bold">⚡ +50 XP</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Currency</span>
                    <span className="font-bold" style={{ color: '#FCD34D' }}>💰 +5</span>
                  </div>
                </div>
              </div>
      </div>

            {/* Center & Right - Timer Display */}
            <div className="lg:col-span-2 space-y-4">
              {/* Timer Card */}
              <div 
                className={`bg-gradient-to-b ${mode === 'work' ? 'from-blue-950/50 via-blue-950/40 to-blue-950/60' : 'from-purple-950/50 via-purple-950/40 to-purple-950/60'} backdrop-blur-sm border-t-2 border-l-2 ${mode === 'work' ? 'border-blue-500/40' : 'border-purple-500/40'} border-r-2 border-b-2 ${mode === 'work' ? 'border-r-blue-700/60 border-b-blue-700/60' : 'border-r-purple-700/60 border-b-purple-700/60'} rounded-3xl p-4 sm:p-8 relative overflow-hidden`}
                style={{
                  boxShadow: mode === 'work' 
                    ? '0 8px 24px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.1)'
                    : '0 8px 24px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(168, 85, 247, 0.1)'
                }}
              >
      {/* Timer Display */}
                <div className="relative z-10 text-center mb-6 sm:mb-8">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className={`w-3 h-3 rounded-full ${
                      isRunning ? 'animate-pulse' : ''
                    } ${mode === 'work' ? 'bg-system-blue' : 'bg-system-purple'}`}></div>
                    <span className={`font-rajdhani text-sm uppercase tracking-widest ${
                      mode === 'work' ? 'text-blue-300' : 'text-purple-300'
                    }`}>
                      {mode === 'work' ? t.focus.focusTime : t.focus.breakTime}
                    </span>
                  </div>
                  
                  {/* Large Timer */}
                  <div className="flex items-center justify-center gap-3 sm:gap-4">
                    <div 
                      className="font-orbitron font-black"
                      style={{
                        fontSize: 'clamp(3rem, 12vw, 8rem)',
                        color: mode === 'work' ? '#6996EE' : '#B382E8',
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                        filter: 'drop-shadow(0 2px 2px rgba(0, 0, 0, 0.3))',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      {time.minutes}
                    </div>
                    
                    <div 
                      className={`font-orbitron font-black ${isRunning ? 'animate-pulse' : ''}`}
                      style={{
                        fontSize: 'clamp(2.5rem, 10vw, 6rem)',
                        color: mode === 'work' ? '#6D96EC' : '#B986EA',
                        lineHeight: 1,
                        opacity: 0.35
                      }}
                    >
                      :
                    </div>
                    
                    <div 
                      className="font-orbitron font-black"
                      style={{
                        fontSize: 'clamp(3rem, 12vw, 8rem)',
                        color: mode === 'work' ? '#6996EE' : '#B382E8',
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                        filter: 'drop-shadow(0 2px 2px rgba(0, 0, 0, 0.3))',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      {time.seconds}
                    </div>
                  </div>
                </div>

                {/* Linear Progress Bar */}
                <div className="relative z-10 mb-4 sm:mb-6">
                  <div className="flex items-center justify-between text-xs font-rajdhani text-gray-400 mb-2">
                    <span>PROGRESS</span>
                    <span className="font-orbitron font-bold">{Math.round(progress)}%</span>
                  </div>
                  <div className="relative h-3 bg-system-dark rounded-full overflow-hidden border border-gray-700">
                  <div 
                    className={`h-full ${
                      mode === 'work' 
                        ? 'bg-gradient-to-r from-blue-500/70 via-blue-400/60 to-blue-500/70' 
                        : 'bg-gradient-to-r from-purple-500/70 via-purple-400/60 to-purple-500/70'
                    } transition-all duration-1000 ease-linear relative`}
                    style={{ width: `${progress}%` }}
                  >
                    <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                  </div>
                  </div>
                </div>

                {/* Control Buttons */}
                <div className="relative z-10 flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => {
                      if (!isRunning && mode === 'work') {
                        soundService.focusStart();
                      }
                      hapticService.click();
                      setIsRunning(!isRunning);
                    }}
                    className={`flex-1 px-6 py-3 sm:px-8 sm:py-4 rounded-2xl font-orbitron font-bold text-base sm:text-lg tracking-wider transition-all hover:scale-105 border-t-2 border-l-2 border-r-2 border-b-2 relative ${
                      isRunning
                        ? 'bg-gradient-to-b from-red-800/60 via-red-900/50 to-red-950/60 border-red-500/50 border-r-red-700/70 border-b-red-700/70 text-white'
                        : mode === 'work'
                        ? 'bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 border-blue-500/50 border-r-blue-700/70 border-b-blue-700/70 text-white'
                        : 'bg-gradient-to-b from-purple-800/60 via-purple-900/50 to-purple-950/60 border-purple-500/50 border-r-purple-700/70 border-b-purple-700/70 text-white'
                    }`}
                    style={{
                      boxShadow: isRunning
                        ? '0 6px 20px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(220, 38, 38, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                        : mode === 'work'
                        ? '0 6px 20px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(59, 130, 246, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                        : '0 6px 20px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(168, 85, 247, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                    }}
                  >
                    <span className="relative z-10">{isRunning ? '⏸️ PAUSE' : '▶️ START'}</span>
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
                  </button>
                  
                  <button
                    onClick={() => {
                      hapticService.tap();
                      setTimeLeft(mode === 'work' ? workDuration : breakDuration);
                      setIsRunning(false);
                    }}
                    className="px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl font-orbitron font-bold text-base sm:text-lg tracking-wider text-gray-300 hover:text-white transition-all hover:scale-105 relative"
                    style={{
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.06), inset 0 -1px 3px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <span className="relative z-10">🔄 RESET</span>
                  </button>
                </div>
              </div>

              {/* Quote Card */}
              <div 
                className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6"
                style={{
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="text-center">
                  <span className="font-rajdhani text-sm sm:text-base italic text-gray-300 leading-relaxed">
                    "{currentQuote}"
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Mode - Clean Immersive */}
      {isFullscreen && (
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Exit Button */}
          <button
            onClick={() => {
              hapticService.tap();
              setIsFullscreen(false);
            }}
            className="absolute top-8 right-8 z-20 w-14 h-14 rounded-full bg-white/5 border-2 border-white/10 hover:border-white/20 text-gray-400 hover:text-white flex items-center justify-center transition-all duration-300 backdrop-blur-xl hover:scale-110 group"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Mode Pills */}
          <div className="absolute top-8 left-1/2 transform -translate-x-1/2 flex gap-4">
            <button
              onClick={() => {
                if (mode !== 'work') {
                  hapticService.tap();
                  setMode('work');
                  setTimeLeft(workDuration);
                  setIsRunning(false);
                }
              }}
              className={`px-8 py-3 rounded-full font-orbitron text-base font-bold tracking-wider transition-all duration-300 backdrop-blur-xl ${
                mode === 'work'
                  ? 'bg-system-blue/15 text-system-blue border-2 border-system-blue shadow-[0_0_25px_rgba(59,130,246,0.25)] scale-110'
                  : 'bg-white/5 text-gray-400 border-2 border-white/10 hover:border-white/20 hover:scale-105'
              }`}
            >
              💼 FOCUS
          </button>
          <button
            onClick={() => {
                if (mode !== 'break') {
                  hapticService.tap();
                  setMode('break');
                  setTimeLeft(breakDuration);
              setIsRunning(false);
                }
              }}
              className={`px-8 py-3 rounded-full font-orbitron text-base font-bold tracking-wider transition-all duration-300 backdrop-blur-xl ${
                mode === 'break'
                  ? 'bg-system-purple/15 text-system-purple border-2 border-system-purple shadow-[0_0_25px_rgba(168,85,247,0.25)] scale-110'
                  : 'bg-white/5 text-gray-400 border-2 border-white/10 hover:border-white/20 hover:scale-105'
              }`}
            >
              ☕ BREAK
          </button>
        </div>

          {/* Main Content */}
          <div className="relative max-w-7xl w-full px-8">
            {/* Timer */}
            <div className="relative z-10 text-center">
              {/* Status Indicator */}
              <div className="flex items-center justify-center gap-3 mb-12">
                <div className={`w-4 h-4 rounded-full ${
                  isRunning ? 'animate-pulse' : ''
                } ${mode === 'work' ? 'bg-system-blue shadow-[0_0_20px_rgba(59,130,246,0.6)]' : 'bg-system-purple shadow-[0_0_20px_rgba(168,85,247,0.6)]'}`}></div>
                <span className={`font-rajdhani text-xl uppercase tracking-[0.3em] ${
                  mode === 'work' ? 'text-blue-300' : 'text-purple-300'
                }`}>
                  {isRunning ? (mode === 'work' ? 'FOCUSING' : 'RESTING') : 'PAUSED'}
                </span>
              </div>

              {/* Giant Timer */}
              <div className="flex items-center justify-center gap-8 mb-12">
                <div 
                  className="font-orbitron font-black tabular-nums"
                  style={{
                    fontSize: 'clamp(8rem, 25vw, 18rem)',
                    color: mode === 'work' ? '#6B96F2' : '#B089EA',
                    lineHeight: 0.9,
                    letterSpacing: '-0.05em',
                    filter: 'drop-shadow(0 3px 2px rgba(0, 0, 0, 0.4))',
                    textShadow: '0 3px 6px rgba(0, 0, 0, 0.7)'
                  }}
                >
                  {time.minutes}
                </div>
                
                <div 
                  className={`font-orbitron font-black ${isRunning ? 'animate-pulse' : ''}`}
                  style={{
                    fontSize: 'clamp(6rem, 18vw, 12rem)',
                    color: mode === 'work' ? '#6B96F2' : '#B089EA',
                    lineHeight: 0.9,
                    opacity: 0.3
                  }}
                >
                  :
      </div>

                <div 
                  className="font-orbitron font-black tabular-nums"
                  style={{
                    fontSize: 'clamp(8rem, 25vw, 18rem)',
                    color: mode === 'work' ? '#6B96F2' : '#B089EA',
                    lineHeight: 0.9,
                    letterSpacing: '-0.05em',
                    filter: 'drop-shadow(0 3px 2px rgba(0, 0, 0, 0.4))',
                    textShadow: '0 3px 6px rgba(0, 0, 0, 0.7)'
                  }}
                >
                  {time.seconds}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="max-w-3xl mx-auto mb-12">
                <div className="relative h-4 bg-white/5 rounded-full overflow-hidden border border-white/10 backdrop-blur-sm">
                  <div 
                    className={`h-full ${
                      mode === 'work' 
                        ? 'bg-gradient-to-r from-blue-500/60 via-blue-400/50 to-blue-500/60' 
                        : 'bg-gradient-to-r from-purple-500/60 via-purple-400/50 to-purple-500/60'
                    } transition-all duration-1000 ease-linear relative`}
                    style={{ width: `${progress}%` }}
                  >
                    <div className={`absolute inset-0 bg-white/8 ${isRunning ? 'animate-pulse' : ''}`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm font-rajdhani text-gray-500 uppercase tracking-widest">0%</span>
                  <span className={`text-2xl font-orbitron font-black ${
                    mode === 'work' ? 'text-system-blue' : 'text-system-purple'
                  }`}>{Math.round(progress)}%</span>
                  <span className="text-sm font-rajdhani text-gray-500 uppercase tracking-widest">100%</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-6">
                <button
                  onClick={() => {
                    if (!isRunning && mode === 'work') {
                      soundService.focusStart();
                    }
                    hapticService.click();
                    setIsRunning(!isRunning);
                  }}
                  className={`px-16 py-5 rounded-2xl font-orbitron font-bold text-xl tracking-wider transition-all duration-300 backdrop-blur-xl ${
                    isRunning
                      ? 'bg-red-600/12 hover:bg-red-600/20 text-red-200 border-2 border-red-400/50 hover:shadow-[0_0_30px_rgba(220,38,38,0.3)]'
                      : mode === 'work'
                      ? 'bg-system-blue/12 hover:bg-system-blue/20 text-system-blue/90 border-2 border-system-blue shadow-[0_0_25px_rgba(59,130,246,0.25)] hover:shadow-[0_0_35px_rgba(59,130,246,0.4)]'
                      : 'bg-system-purple/12 hover:bg-system-purple/20 text-system-purple/90 border-2 border-system-purple shadow-[0_0_25px_rgba(168,85,247,0.25)] hover:shadow-[0_0_35px_rgba(168,85,247,0.4)]'
                  } hover:scale-110`}
                >
                  {isRunning ? '⏸ PAUSE' : '▶ START'}
                </button>
                
                {!isRunning && (
                  <button
                    onClick={() => {
                      hapticService.tap();
                      setTimeLeft(mode === 'work' ? workDuration : breakDuration);
                    }}
                    className="px-12 py-5 rounded-2xl font-orbitron font-bold text-xl tracking-wider bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border-2 border-white/10 hover:border-white/20 transition-all duration-300 backdrop-blur-xl hover:scale-110"
                  >
                    🔄 RESET
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 max-w-3xl text-center px-8">
            <div className="font-rajdhani text-xl italic text-gray-300 tracking-wide leading-relaxed backdrop-blur-sm bg-black/20 px-8 py-4 rounded-2xl border border-white/5">
              "{currentQuote}"
            </div>
        </div>
        </div>
      )}
      
    </div>
  );
};

export default FocusPage;
